import type {LabelFormat} from './format'
import type {LabelSubstanceHeading, LabelSubstanceHeadingSize, LabelSubstanceInput} from './types'

// widest heading that fits one line at the heading size: measured with Satoshi bold, all-caps names such as
// "5-FLUOROURACIL" fit 32 characters, mixed case about 38 (CY-299)
export const HEADING_ONE_LINE_MAX = 32

/** heading font size in label px per size; the PDF uses the same values */
export const HEADING_FONT_PX: Record<LabelSubstanceHeadingSize, number> = {
  regular: 32,
  twoLines: 24,
  twoLinesSmall: 20,
}

/** characters per line at a size, scaled from the 32 px measurement and rounded down to stay on the safe side */
export function headingLineCapacity(size: LabelSubstanceHeadingSize, oneLineMax = HEADING_ONE_LINE_MAX) {
  return Math.floor((oneLineMax * HEADING_FONT_PX.regular) / HEADING_FONT_PX[size])
}

function shorten(text: string, room: number) {
  if (text.length <= room) return text
  const cut = text.slice(0, Math.max(room, 1))
  // keep a word whole when the cut ends on a word boundary, else go back to the last space unless that
  // would throw away more than half the room
  const lastSpace = cut.lastIndexOf(' ')
  const shortened = text[cut.length] === ' ' || lastSpace <= room / 2 ? cut : cut.slice(0, lastSpace)
  return `${shortened.trimEnd()}…`
}

/**
 * Shortens the name so "name dose" fits two lines; the dose is never cut. A line break can push at most one word to
 * the next line, so two lines hold two lines' worth of characters minus the longest word.
 */
function fitToTwoLines(heading: LabelSubstanceHeading, size: LabelSubstanceHeadingSize, oneLineMax: number) {
  const {name, dose} = heading
  const longestWord = Math.max(...`${name} ${dose}`.split(' ').map(word => word.length))
  const budget = 2 * headingLineCapacity(size, oneLineMax) - longestWord
  if (`${name} ${dose}`.length <= budget) return name
  // room left for the name, its ellipsis and the space before the dose
  return shorten(name, budget - dose.length - 2)
}

/**
 * One "substance dose" heading per drug (CY-299); the trade name goes on the product line below it. A heading longer
 * than one line wraps to two lines at a smaller size, and only the name is shortened when even two lines are not
 * enough, so the dose and its unit are always printed in full. With two drugs both headings take the same, smaller
 * size so the label still fits its 90 mm. oneLineMax is the label size's one-line capacity (CY-421).
 */
export function buildSubstanceHeadings(
  substances: Pick<LabelSubstanceInput, 'name' | 'prescribedDose' | 'doseUnit'>[],
  format: LabelFormat,
  oneLineMax = HEADING_ONE_LINE_MAX
): {headings: LabelSubstanceHeading[]; size: LabelSubstanceHeadingSize} {
  const parts = substances.map(s => ({name: s.name, dose: `${format.dose(s.prescribedDose)} ${format.unit(s.doseUnit)}`}))

  const fitsOneLine = parts.every(h => `${h.name} ${h.dose}`.length <= oneLineMax)
  const size: LabelSubstanceHeadingSize = fitsOneLine ? 'regular' : parts.length > 1 ? 'twoLinesSmall' : 'twoLines'
  const headings = parts.map(h => ({name: fitsOneLine ? h.name : fitToTwoLines(h, size, oneLineMax), dose: h.dose}))
  return {headings, size}
}
